import type { Cadeia, PalavraColocada } from '@core/tipos';
import { caracteresCanonicos, chaveCelula } from '@core/lexico/normalizacao';
import type { EstadoLetra } from '@core/termo/avaliacao';
import { LADO_CELULA } from '@core/tabuleiro/camera';
import {
  celulaNoIndice,
  celulasDe,
  regiaoDeCelulas,
  unirRegioes,
} from '@core/tabuleiro/coordenadas';

/**
 * O desenho do tabuleiro em canvas, sem decidir o formato de saída (§7.5).
 *
 * Aqui mora o que o PNG e o GIF têm em comum: o enquadramento, a paleta lida
 * do documento e a pintura de uma célula. Os dois formatos precisam produzir
 * exatamente a mesma imagem — o GIF é o PNG revelado letra a letra —, e uma
 * segunda cópia das cores e das medidas divergiria no primeiro ajuste.
 */

/** Respiro em volta do tabuleiro, em células. */
const MARGEM = 0.6;

/** Altura da faixa de rodapé, em células. */
const RODAPE = 0.9;

const RAIO = 6;

export { LADO_CELULA };

export interface PaletaImagem {
  readonly fundo: string;
  readonly tinta: string;
  readonly tintaSuave: string;
  readonly papelElevado: string;
  readonly papelBorda: string;
  readonly historico: string;
  readonly historicoFundo: string;
  readonly ponte: string;
  readonly ponteFundo: string;
  readonly ponteBorda: string;
  readonly certa: string;
  readonly presente: string;
  readonly sugestaoFundo: string;
  readonly sugestaoBorda: string;
  readonly fonteTabuleiro: string;
  readonly fonteUi: string;
}

/** Lê a paleta em vigor no documento — tema claro ou escuro, o que estiver. */
export function paletaDoDocumento(raiz: HTMLElement = document.documentElement): PaletaImagem {
  const estilo = getComputedStyle(raiz);
  const cor = (nome: string, reserva: string) => {
    const valor = estilo.getPropertyValue(nome).trim();
    return valor === '' ? reserva : valor;
  };

  return {
    fundo: cor('--papel', '#faf6f0'),
    tinta: cor('--tinta', '#1f1b16'),
    tintaSuave: cor('--tinta-suave', '#6b6153'),
    papelElevado: cor('--papel-elevado', '#fffdf9'),
    papelBorda: cor('--papel-borda', '#e2d8ca'),
    historico: cor('--historico', '#4a4239'),
    historicoFundo: cor('--historico-fundo', '#f6f0e7'),
    ponte: cor('--ponte', '#2c6355'),
    ponteFundo: cor('--ponte-fundo', '#e3ede9'),
    ponteBorda: cor('--ponte-borda', '#a9c8bf'),
    certa: cor('--certa', '#2c6355'),
    presente: cor('--presente', '#a97318'),
    sugestaoFundo: cor('--sugestao-fundo', '#f1ebe1'),
    sugestaoBorda: cor('--sugestao-borda', '#ded3c4'),
    fonteTabuleiro: cor('--fonte-tabuleiro', 'Georgia, serif'),
    fonteUi: cor('--fonte-ui', 'system-ui, sans-serif'),
  };
}

interface Pintura {
  readonly fundo: string;
  readonly borda: string;
  readonly texto: string;
}

function pinturaDaCelula(
  palavra: PalavraColocada,
  marca: EstadoLetra | undefined,
  p: PaletaImagem,
): Pintura {
  // §23.3: a marca do dia manda sobre o papel da palavra — é a informação do
  // modo, e é por isso que ela também vence o cruzamento na tela.
  if (marca === 'certa') return { fundo: p.certa, borda: p.certa, texto: p.papelElevado };
  if (marca === 'presente') return { fundo: p.presente, borda: p.presente, texto: p.papelElevado };
  if (marca === 'ausente') {
    return { fundo: p.sugestaoFundo, borda: p.sugestaoBorda, texto: p.tintaSuave };
  }

  if (palavra.papel === 'ponte')
    return { fundo: p.ponteFundo, borda: p.ponteBorda, texto: p.ponte };
  // A primeira palavra é de onde o tabuleiro saiu, e não desbota (§15, T07-09).
  if (palavra.turno === 0) {
    return { fundo: p.papelElevado, borda: p.papelBorda, texto: p.tinta };
  }
  return { fundo: p.historicoFundo, borda: p.historicoFundo, texto: p.historico };
}

/** Medidas da imagem, em unidades de desenho (antes de qualquer escala). */
export interface Enquadramento {
  readonly largura: number;
  readonly altura: number;
  /** Quanto somar à coordenada de grade para chegar ao pixel. */
  readonly deslocX: number;
  readonly deslocY: number;
}

/**
 * O retângulo que cabe a cadeia inteira.
 *
 * Sai da cadeia, e não da tela: o tabuleiro exibido é virtualizado (§16) e
 * recortado pela câmera (§7.2), então capturar a tela traria só o pedaço
 * visível. E a origem entra na conta porque a cadeia cresce para os quatro
 * lados — metade dos tabuleiros tem coluna ou linha negativa.
 */
export function enquadrar(cadeia: Cadeia): Enquadramento {
  const celulas = cadeia.palavras.flatMap((p) => [...celulasDe(p)]);

  const regiao =
    celulas.length === 0
      ? { colunaMin: 0, linhaMin: 0, colunaMax: 0, linhaMax: 0 }
      : celulas.map((c) => regiaoDeCelulas([c])).reduce(unirRegioes);

  const colunas = regiao.colunaMax - regiao.colunaMin + 1;
  const linhas = regiao.linhaMax - regiao.linhaMin + 1;

  return {
    largura: (colunas + MARGEM * 2) * LADO_CELULA,
    altura: (linhas + MARGEM * 2 + RODAPE) * LADO_CELULA,
    deslocX: (MARGEM - regiao.colunaMin) * LADO_CELULA,
    deslocY: (MARGEM - regiao.linhaMin) * LADO_CELULA,
  };
}

/** Uma célula pronta para pintar: já resolvida em pixels, cor e letra. */
export interface CelulaDesenho {
  readonly x: number;
  readonly y: number;
  readonly letra: string;
  readonly pintura: Pintura;
  /** §23: a célula carrega marca do dia, e por isso vence o cruzamento. */
  readonly marcada: boolean;
  readonly chave: string;
}

/**
 * As células na ordem em que a cadeia foi jogada.
 *
 * É a ordem de digitação, que é o que o GIF precisa revelar. A ordem de
 * **pintura** é outra — as marcadas por último, para a ponte não apagar a
 * marca do cruzamento —, e quem concilia as duas é `venceOCruzamento`.
 */
export function celulasEmOrdem(
  cadeia: Cadeia,
  marcas: ReadonlyMap<string, readonly EstadoLetra[]> | undefined,
  enq: Enquadramento,
  paleta: PaletaImagem,
): readonly CelulaDesenho[] {
  const saida: CelulaDesenho[] = [];

  for (const palavra of cadeia.palavras) {
    const letras = caracteresCanonicos(palavra.texto);
    const daPalavra = marcas?.get(palavra.id);

    letras.forEach((letra, i) => {
      const c = celulaNoIndice(palavra.origem, palavra.orientacao, i);
      const marca = daPalavra?.[i];
      saida.push({
        x: c.coluna * LADO_CELULA + enq.deslocX,
        y: c.linha * LADO_CELULA + enq.deslocY,
        letra,
        pintura: pinturaDaCelula(palavra, marca, paleta),
        marcada: marca !== undefined,
        chave: chaveCelula(c.coluna, c.linha),
      });
    });
  }

  return saida;
}

/**
 * Esta célula pode ser pintada agora, ou uma marca já ocupou o lugar?
 *
 * §23.3: a marca do dia é a única informação do modo e não pode ser coberta
 * pela ponte, que nasce um turno depois da base que atravessa. Na imagem
 * estática isso vira ordem de pintura; aqui, onde as células aparecem uma a
 * uma, vira esta pergunta.
 */
export function venceOCruzamento(celula: CelulaDesenho, marcadasJaPintadas: ReadonlySet<string>) {
  return celula.marcada || !marcadasJaPintadas.has(celula.chave);
}

export function pintarFundo(
  ctx: CanvasRenderingContext2D,
  enq: Enquadramento,
  paleta: PaletaImagem,
): void {
  ctx.fillStyle = paleta.fundo;
  ctx.fillRect(0, 0, enq.largura, enq.altura);
}

export function pintarCelula(
  ctx: CanvasRenderingContext2D,
  celula: CelulaDesenho,
  paleta: PaletaImagem,
): void {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Um fio de folga entre células, como o tabuleiro tem.
  ctx.beginPath();
  ctx.roundRect(celula.x + 1, celula.y + 1, LADO_CELULA - 2, LADO_CELULA - 2, RAIO);
  ctx.fillStyle = celula.pintura.fundo;
  ctx.fill();
  ctx.strokeStyle = celula.pintura.borda;
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = celula.pintura.texto;
  ctx.font = `600 ${LADO_CELULA * 0.46}px ${paleta.fonteTabuleiro}`;
  ctx.fillText(celula.letra, celula.x + LADO_CELULA / 2, celula.y + LADO_CELULA / 2 + 1);
}

export function pintarLegenda(
  ctx: CanvasRenderingContext2D,
  enq: Enquadramento,
  legenda: string,
  paleta: PaletaImagem,
): void {
  ctx.fillStyle = paleta.tintaSuave;
  ctx.font = `500 ${LADO_CELULA * 0.28}px ${paleta.fonteUi}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(legenda, enq.largura / 2, enq.altura - (MARGEM * LADO_CELULA) / 2 - 2);
}

/** Retângulo que contém uma leva de células, com a folga da borda. */
export function caixaDe(celulas: readonly CelulaDesenho[]): {
  x: number;
  y: number;
  largura: number;
  altura: number;
} {
  const x = Math.min(...celulas.map((c) => c.x));
  const y = Math.min(...celulas.map((c) => c.y));
  const direita = Math.max(...celulas.map((c) => c.x)) + LADO_CELULA;
  const baixo = Math.max(...celulas.map((c) => c.y)) + LADO_CELULA;
  return { x, y, largura: direita - x, altura: baixo - y };
}
